<template>
  <fieldset :id="'step' + formStore.currentStep + 'Fieldset'" class="u-spaceItems3">
    <CardContent>
      <legend>
        <h2 class="u-textItalic u-textGrow2 Groove u-padBottom04">
          <span>Location Information</span>
        </h2>
      </legend>
      <div>
        <ul class="u-listReset u-spaceItems1">
          <li
            v-for="(location, index) in locations.filter((location) => !location.isFromTinSearch)"
            :key="`${location.locationId}-${renderKey}`"
            class="location-item"
          >
            <Collapse
              :id="'locationCollapse' + index"
              :initiallyExpanded="shouldExpandLocation(location, index)"
            >
              <template #label>
                <div class="u-flexBetween u-flexAlignCenter">
                  <span class="u-textBlackAlt"
                    ><span v-if="location.locationName !== ''">{{ location.locationName }}</span
                    ><span v-else>Location {{ index + 1 }} Details</span></span
                  >
                </div>
              </template>
              <template #content>
                <!-- Location & Practice Name -->
                <fieldset class="u-spaceItems1">
                  <legend>
                    <h4>Location & Practice Name</h4>
                  </legend>

                  <div class="u-Grid Grid--withGap">
                    <div class="Grid-cell">
                      <BaseInput
                        :id="'locationName' + index"
                        :name="`locations[${index}].locationName`"
                        v-model="location.locationName"
                        label="Location Name"
                        placeholder="Enter location name"
                        required
                        minlength="2"
                        max="100"
                      />
                      <!-- Hidden input for location ID -->
                      <input
                        type="hidden"
                        :id="'locationId' + index"
                        :name="'locationId' + index"
                        :value="location.locationId"
                      />
                    </div>
                    <div class="Grid-cell">
                      <BaseSelect
                        :id="'locationPractice' + index"
                        :name="'locationPractice' + index"
                        v-model="location.practiceId"
                        label="Associated Practice"
                        :options="validPracticeOptions"
                        option-value="value"
                        option-label="label"
                        placeholder="Select a practice"
                        required
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Location Phone & Fax Info -->
                <fieldset class="u-spaceItems1">
                  <legend>Location Phone & Fax Information</legend>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-size3of5">
                      <BaseInput
                        :id="'locationPhone' + index"
                        :name="'locationPhone' + index"
                        v-model="location.phone"
                        label="Phone Number"
                        type="tel"
                        placeholder="Enter phone number"
                        mask="###-###-####"
                        required="required"
                        help-text="Format: 123-456-7890"
                      />
                    </div>
                    <div class="Grid-cell u-size2of5">
                      <BaseInput
                        :id="'locationExt' + index"
                        :name="'locationExt' + index"
                        v-model="location.ext"
                        label="Ext"
                        type="text"
                        max="10"
                      />
                    </div>
                    <div class="Grid-cell">
                      <BaseInput
                        :id="'locationFax' + index"
                        :name="'locationFax' + index"
                        v-model="location.fax"
                        label="Fax"
                        type="tel"
                        mask="###-###-####"
                        help-text="Format: 123-456-7890"
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Location EMR Info -->
                <fieldset class="u-spaceItems1">
                  <legend class="u-block">Location EMR Information</legend>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'locationEmrVendor' + index"
                        :name="'locationEmrVendor' + index"
                        v-model="location.emr.vendor"
                        label="EMR Vendor"
                        type="text"
                        placeholder="Enter EMR vendor"
                        maxlength="100"
                        min="2"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'locationEmrVersion' + index"
                        :name="'locationEmrVersion' + index"
                        v-model="location.emr.version"
                        label="EMR Version"
                        type="text"
                        placeholder="Enter EMR version"
                        maxlength="25"
                        minlength="2"
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Location Address Info -->
                <fieldset class="u-spaceItems1">
                  <legend>
                    <h4>Location Address Information</h4>
                  </legend>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        v-model="location.fullAddress.address1"
                        :id="'locationAddressStreet' + index"
                        :name="'locationAddressStreet' + index"
                        label="Street Address"
                        required
                        type="text"
                        maxlength="60"
                        minlength="2"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        v-model="location.fullAddress.address2"
                        :id="'locationAddressStreetOptional' + index"
                        :name="'locationAddressStreetOptional' + index"
                        label="Street Address Line 2"
                        type="text"
                        maxlength="20"
                        minlength="2"
                        help-text="Apartment, suite, unit, building, floor, etc."
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of3">
                      <BaseInput
                        v-model="location.fullAddress.city"
                        :id="'locationAddressCity' + index"
                        :name="'locationAddressCity' + index"
                        label="City"
                        type="text"
                        maxlength="30"
                        minlength="2"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of3">
                      <BaseSelect
                        v-model="location.fullAddress.state"
                        :id="'locationAddressState' + index"
                        :name="'locationAddressState' + index"
                        label="State"
                        required="required"
                        :options="states"
                        option-value="code"
                        option-label="name"
                        placeholder="Select a state"
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of3">
                      <BaseInput
                        v-model="location.fullAddress.zip"
                        :id="'locationAddressZip' + index"
                        :name="'locationAddressZip' + index"
                        label="Zip Code"
                        required="required"
                        minlength="5"
                        :pattern="`[0-9]{5}`"
                        mask="#####"
                        help-text="Format: 12345"
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Location Office Manager Info -->
                <fieldset class="u-spaceItems1">
                  <legend>
                    <h4>Location Office Manager Information</h4>
                  </legend>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'officeManagerFirstName' + index"
                        :name="'officeManagerFirstName' + index"
                        v-model="location.officeManager.firstName"
                        label="First Name"
                        type="text"
                        placeholder="Enter first name"
                        maxlength="50"
                        minlength="2"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'officeManagerLastName' + index"
                        :name="'officeManagerLastName' + index"
                        v-model="location.officeManager.lastName"
                        label="Last Name"
                        type="text"
                        placeholder="Enter last name"
                        maxlength="50"
                        minlength="2"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size3of5">
                      <BaseInput
                        :id="'officeManagerPhone' + index"
                        :name="'officeManagerPhone' + index"
                        v-model="location.officeManager.phone"
                        label="Phone Number"
                        type="tel"
                        placeholder="Enter phone number"
                        mask="###-###-####"
                        help-text="Format: 123-456-7890"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size2of5">
                      <BaseInput
                        :id="'officeManagerExt' + index"
                        :name="'officeManagerExt' + index"
                        v-model="location.officeManager.ext"
                        label="Ext"
                        type="text"
                        maxlength="10"
                      />
                    </div>
                    <div class="Grid-cell">
                      <BaseInput
                        :id="'officeManagerEmail' + index"
                        :name="'officeManagerEmail' + index"
                        v-model="location.officeManager.email"
                        label="Email Address"
                        type="email"
                        placeholder="Enter email address"
                        maxlength="100"
                        required
                      />
                    </div>
                  </div>
                </fieldset>
                <hr />
                <!-- Billing Manager Info -->
                <fieldset class="u-spaceItems1">
                  <legend>
                    <h4>Billing Manager Information</h4>
                  </legend>
                  <p class="u-textShrink1 u-textGray">
                    The Location Billing Manager can either be the same as the Office Manager or a
                    separate individual, depending on your preference.
                  </p>
                  <div class="checkbox-container">
                    <label
                      :for="'sameAsOfficeManager' + index"
                      class="Toggle Toggle--checkbox u-textBlackAlt"
                    >
                      <input
                        class="Toggle-input"
                        :id="'sameAsOfficeManager' + index"
                        v-model="location.billingManager.sameAsOfficeManager"
                        type="checkbox"
                        @change="handleBillingManagerCheckbox(location)"
                      />
                      <span class="Toggle-indicator"></span>
                      <span>Is the Billing Manager the same as the Office Manager?</span>
                    </label>
                  </div>
                  <div class="Grid Grid--withGap">
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'billingManagerFirstName' + index"
                        :name="'billingManagerFirstName' + index"
                        v-model="location.billingManager.firstName"
                        label="First Name"
                        type="text"
                        placeholder="Enter first name"
                        maxlength="50"
                        minlength="2"
                        :disabled="location.billingManager.sameAsOfficeManager"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size1of2">
                      <BaseInput
                        :id="'billingManagerLastName' + index"
                        :name="'billingManagerLastName' + index"
                        v-model="location.billingManager.lastName"
                        label="Last Name"
                        type="text"
                        placeholder="Enter last name"
                        maxlength="50"
                        minlength="2"
                        :disabled="location.billingManager.sameAsOfficeManager"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size3of5">
                      <BaseInput
                        :id="'billingManagerPhone' + index"
                        :name="'billingManagerPhone' + index"
                        v-model="location.billingManager.phone"
                        label="Phone Number"
                        type="tel"
                        placeholder="Enter phone number"
                        mask="###-###-####"
                        help-text="Format: 123-456-7890"
                        :disabled="location.billingManager.sameAsOfficeManager"
                        required
                      />
                    </div>
                    <div class="Grid-cell u-sm-size2of5">
                      <BaseInput
                        :id="'billingManagerExt' + index"
                        :name="'billingManagerExt' + index"
                        v-model="location.billingManager.ext"
                        label="Ext"
                        type="text"
                        maxlength="10"
                        :disabled="location.billingManager.sameAsOfficeManager"
                      />
                    </div>
                    <div class="Grid-cell">
                      <BaseInput
                        :id="'billingManagerEmail' + index"
                        :name="'billingManagerEmail' + index"
                        v-model="location.billingManager.email"
                        label="Email Address"
                        type="email"
                        placeholder="Enter email address"
                        maxlength="100"
                        :disabled="location.billingManager.sameAsOfficeManager"
                        required
                      />
                    </div>
                  </div>
                </fieldset>
              </template>
              <template #removeButton>
                <div class="u-spaceLeft03 u-spaceTop03">
                  <button
                    v-if="locations.filter((location) => !location.isFromTinSearch).length > 1"
                    @click.stop="removeLocation(index)"
                    type="button"
                    class="Button Button--compact Button--link u-textRed u-textShrink1 u-flexExpandLeft"
                    title="Remove this location"
                    :disabled="location.isReadOnly"
                  >
                    <span>
                      <svg
                        class="Icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M18.01,20.132,3.868,5.99A1.5,1.5,0,0,1,5.99,3.868L20.132,18.01a1.5,1.5,0,1,1-2.121,2.121Z"
                        />
                        <path
                          d="M3.868,20.132a1.5,1.5,0,0,1,0-2.121L18.01,3.868A1.5,1.5,0,1,1,20.132,5.99L5.99,20.132A1.5,1.5,0,0,1,3.868,20.132Z"
                        />
                      </svg>
                    </span>
                    <span> Remove<span class="u-hiddenVisually"> Location</span> </span>
                  </button>
                </div>
              </template>
            </Collapse>
          </li>
        </ul>
      </div>
      <div class="u-spaceTop2">
        <button @click="addLocation" type="button" class="Button Button--compact Button--tertiary">
          <span>
            <svg
              class="Icon u-textColorInherit u-textShrink2"
              role="presentation"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
            >
              <path
                d="M22,13.5H2a1.5,1.5,0,0,1,0-3H22a1.5,1.5,0,0,1,0,3Z"
                class="Icon-rotateCounter1of8"
              />
              <path
                d="M12,23.5A1.50007,1.50007,0,0,1,10.5,22V2a1.5,1.5,0,0,1,3,0V22A1.50039,1.50039,0,0,1,12,23.5Z"
                class="Icon-rotateCounter1of8"
              />
            </svg>
          </span>
          Add Location
        </button>
      </div>
    </CardContent>
  </fieldset>
</template>

<script setup>
import { computed, watch, onMounted, ref, nextTick } from 'vue'
import { useFormStore } from '../../stores/formStore'
import states from '../../data/states.json'

import CardContent from '../CardContent.vue'
import Collapse from '../Collapse.vue'
import BaseInput from '../fields/BaseInput.vue'
import BaseSelect from '../fields/BaseSelect.vue'

const formStore = useFormStore()

// Define emit for validity changes
const emit = defineEmits(['validity-changed'])

// Local reactive ref for locations (much simpler approach)
const locations = ref([])

// Initialize locations from store on mount
const initializeLocations = () => {
  locations.value = []
  // Collect all locations from all practices
  formStore.membershipApplicationInfo.practices.forEach((practice) => {
    practice.locations.forEach((location) => {
      locations.value.push({
        ...location,
        _originalPracticeId: practice.practiceId, // Track original practice
      })
    })
  })
  console.log('Initialized locations:', locations.value)
  console.log(
    'Locations with isFromTinSearch flags:',
    locations.value.map((loc) => ({
      name: loc.locationName,
      isFromTinSearch: loc.isFromTinSearch,
      id: loc.locationId,
    })),
  )
}

// Sync location back to correct practice when practiceId changes
const syncLocationToPractice = (location) => {
  if (!location.practiceId) return

  console.log(`Syncing location ${location.locationName} to practice ${location.practiceId}`)

  // Remove from old practice if it exists
  if (location._originalPracticeId && location._originalPracticeId !== location.practiceId) {
    const oldPractice = formStore.membershipApplicationInfo.practices.find(
      (p) => p.practiceId == location._originalPracticeId,
    )
    if (oldPractice) {
      const index = oldPractice.locations.findIndex((loc) => loc.locationId == location.locationId)
      if (index !== -1) {
        oldPractice.locations.splice(index, 1)
        console.log(`Removed location from old practice ${oldPractice.practiceName}`)
      }
    }
  }

  // Add to new practice
  const newPractice = formStore.membershipApplicationInfo.practices.find(
    (p) => p.practiceId == location.practiceId,
  )
  if (newPractice) {
    const existingLocationIndex = newPractice.locations.findIndex(
      (loc) => loc.locationId == location.locationId,
    )
    const cleanLocation = { ...location }
    delete cleanLocation._originalPracticeId // Remove tracking property

    if (existingLocationIndex !== -1) {
      // Update existing location
      newPractice.locations[existingLocationIndex] = cleanLocation
    } else {
      // Add new location
      newPractice.locations.push(cleanLocation)
    }

    location._originalPracticeId = location.practiceId // Update tracking
    console.log(`Synced location ${location.locationName} to practice ${newPractice.practiceName}`)
  }
}

// Watch for practiceId changes and sync to store
watch(
  () => locations.value.map((loc) => loc.practiceId),
  (newPracticeIds, oldPracticeIds) => {
    newPracticeIds.forEach((newId, index) => {
      const oldId = oldPracticeIds[index]
      if (newId !== oldId && newId) {
        syncLocationToPractice(locations.value[index])
      }
    })
  },
  { deep: true },
)

// Track which location should be expanded (only the most recently added)
const lastAddedLocationId = ref(null)
// Force re-render key
const renderKey = ref(0)

// Function to determine if a location should be expanded
const shouldExpandLocation = (location, index) => {
  // Get only user-editable locations (filtered list that's actually being displayed)
  const userEditableLocations = locations.value.filter((loc) => !loc.isFromTinSearch)

  // If there's only one user-editable location, it should always be expanded
  if (userEditableLocations.length === 1) {
    return true
  }

  // If this location was the last one added
  if (lastAddedLocationId.value == location.locationId) {
    return true
  }

  // If this is the first user-editable location (index 0 in the filtered array) and no specific location is marked
  if (index === 0 && !lastAddedLocationId.value) {
    return true
  }

  return false
}

// Use data sync composables

// Handle checkbox change event - using the sync functionality
const handleBillingManagerCheckbox = (location) => {
  // Trigger the sync when checkbox changes
  if (location.billingManager.sameAsOfficeManager) {
    // Copy all office manager fields to billing manager
    location.billingManager.firstName = location.officeManager.firstName
    location.billingManager.lastName = location.officeManager.lastName
    location.billingManager.phone = location.officeManager.phone
    location.billingManager.ext = location.officeManager.ext
    location.billingManager.email = location.officeManager.email
  } else {
    // Clear billing manager fields when unchecked
    location.billingManager.firstName = ''
    location.billingManager.lastName = ''
    location.billingManager.phone = ''
    location.billingManager.ext = ''
    location.billingManager.email = ''
  }
  console.log(
    'Synced billing manager for location:',
    location.locationId,
    'checked:',
    location.billingManager.sameAsOfficeManager,
  )
}

// Ensure all existing locations have IDs
const ensureLocationIds = () => {
  // The location management composable handles ID generation
  // This function is now handled by the local initializeLocations method
  initializeLocations()
}

// Run this when component is mounted
ensureLocationIds()

// Location removal is now handled by the location management composable and store methods

// Function to add a new location
const addLocation = () => {
  // Create new location directly
  const newLocation = {
    locationId: formStore.generateId(),
    locationName: '',
    phone: '',
    ext: '',
    fax: '',
    fullAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    officeManager: {
      firstName: '',
      lastName: '',
      phone: '',
      ext: '',
      email: '',
    },
    billingManager: {
      sameAsOfficeManager: false,
      firstName: '',
      lastName: '',
      phone: '',
      ext: '',
      email: '',
    },
    emr: {
      vendor: '',
      version: '',
    },
    practiceId: '',
  }

  // Ensure isFromTinSearch is explicitly set to false for user-added locations
  newLocation.isFromTinSearch = false
  newLocation.isReadOnly = false

  console.log('Creating new location with numerical ID:', newLocation.locationId)

  // Add location to local ref without auto-assigning practice
  // User must explicitly select a practice to prevent oversight errors
  locations.value.push(newLocation)
  console.log(`Added location to local ref with numerical ID: ${newLocation.locationId} - practice must be selected by user`)

  // Mark this as the last added location (this will cause only it to be expanded)
  lastAddedLocationId.value = newLocation.locationId

  // Force re-render to update collapse states
  renderKey.value++
}

// Computed property for valid practice options
const validPracticeOptions = computed(() => {
  return formStore.membershipApplicationInfo.practices
    .filter(
      (practice) =>
        practice.practiceName &&
        practice.practiceName.trim() !== '' &&
        practice.practiceId != null &&
        practice.practiceId !== '',
    )
    .map((practice) => ({
      value: practice.practiceId,
      label: practice.practiceName,
    }))
})

// Direct step validation
const isStep4Valid = computed(() => {
  // Get user-editable locations (non-TIN search)
  const userEditableLocations = locations.value.filter((loc) => !loc.isFromTinSearch)

  // Must have at least one location if the application type requires it
  if (formStore.applicationType !== 'new' && userEditableLocations.length === 0) {
    // If not a new application, having no locations can be valid
    return true
  }
  if (userEditableLocations.length === 0) {
    return false
  }

  // All locations must be valid
  return userEditableLocations.every((location) => {
    // Check required fields
    const hasLocationName = location.locationName && location.locationName.trim().length >= 2
    const hasPracticeId = location.practiceId != null && location.practiceId !== ''
    const hasPhone = location.phone && location.phone.trim().length > 0

    // Office Manager validation
    const hasOfficeManagerFirstName =
      location.officeManager.firstName && location.officeManager.firstName.trim().length >= 2
    const hasOfficeManagerLastName =
      location.officeManager.lastName && location.officeManager.lastName.trim().length >= 2
    const hasOfficeManagerPhone =
      location.officeManager.phone && location.officeManager.phone.trim().length > 0
    const hasOfficeManagerEmail =
      location.officeManager.email && location.officeManager.email.trim().length > 0

    // Billing Manager validation
    let hasBillingManagerInfo = true
    if (!location.billingManager.sameAsOfficeManager) {
      hasBillingManagerInfo =
        location.billingManager.firstName &&
        location.billingManager.firstName.trim().length >= 2 &&
        location.billingManager.lastName &&
        location.billingManager.lastName.trim().length >= 2 &&
        location.billingManager.phone &&
        location.billingManager.phone.trim().length > 0 &&
        location.billingManager.email &&
        location.billingManager.email.trim().length > 0
    }

    return (
      hasLocationName &&
      hasPracticeId &&
      hasPhone &&
      hasOfficeManagerFirstName &&
      hasOfficeManagerLastName &&
      hasOfficeManagerPhone &&
      hasOfficeManagerEmail &&
      hasBillingManagerInfo
    )
  })
})

// Watch for validation changes and emit to parent
watch(
  isStep4Valid,
  (newValid) => {
    // Update store step validation
    formStore.setStepValidation(4, newValid)
    // Emit to parent component
    emit('validity-changed', newValid)
  },
  { immediate: true },
)

// Watch for office manager changes and sync to billing manager when checkbox is checked
watch(
  () =>
    locations.value.map((loc) => ({
      locationId: loc.locationId,
      officeManager: loc.officeManager,
      sameAsOfficeManager: loc.billingManager.sameAsOfficeManager,
    })),
  (newLocationData) => {
    newLocationData.forEach((locationData) => {
      const location = locations.value.find((loc) => loc.locationId == locationData.locationId)
      if (location && location.billingManager.sameAsOfficeManager) {
        // Sync office manager data to billing manager
        location.billingManager.firstName = location.officeManager.firstName
        location.billingManager.lastName = location.officeManager.lastName
        location.billingManager.phone = location.officeManager.phone
        location.billingManager.ext = location.officeManager.ext
        location.billingManager.email = location.officeManager.email
      }
    })
  },
  { deep: true },
)

// Watch for changes in local locations and sync to store
watch(
  locations,
  (newLocations) => {
    // Sync all locations back to their respective practices in the store
    newLocations.forEach((location) => {
      if (location.practiceId) {
        const practice = formStore.membershipApplicationInfo.practices.find(
          (p) => p.practiceId == location.practiceId,
        )
        if (practice) {
          // Find the location in the practice
          const storeLocationIndex = practice.locations.findIndex(
            (loc) => loc.locationId == location.locationId,
          )
          if (storeLocationIndex !== -1) {
            // Update the existing location in the store
            const cleanLocation = { ...location }
            delete cleanLocation._originalPracticeId // Remove tracking property
            practice.locations[storeLocationIndex] = cleanLocation
          } else {
            // Add new location to practice if it doesn't exist
            const cleanLocation = { ...location }
            delete cleanLocation._originalPracticeId // Remove tracking property
            practice.locations.push(cleanLocation)
          }
        }
      }
    })
  },
  { deep: true },
)

// Also sync when individual location properties change
watch(
  () =>
    locations.value.map((loc) => ({
      locationId: loc.locationId,
      locationName: loc.locationName,
      phone: loc.phone,
      ext: loc.ext,
      fax: loc.fax,
      fullAddress: { ...loc.fullAddress },
      officeManager: { ...loc.officeManager },
      billingManager: { ...loc.billingManager },
      emr: { ...loc.emr },
      practiceId: loc.practiceId,
    })),
  (newLocationData) => {
    // Sync individual field changes back to store
    newLocationData.forEach((locationData, index) => {
      const localLocation = locations.value[index]
      if (localLocation && localLocation.practiceId) {
        const practice = formStore.membershipApplicationInfo.practices.find(
          (p) => p.practiceId === localLocation.practiceId,
        )
        if (practice) {
          const storeLocation = practice.locations.find(
            (loc) => loc.locationId === localLocation.locationId,
          )
          if (storeLocation) {
            // Update store location with new data
            Object.assign(storeLocation, locationData)
            console.log(`Synced location ${locationData.locationName} to store`)
          }
        }
      }
    })
  },
  { deep: true },
)

// Set up initial state on mount
onMounted(() => {
  console.log('StepFourLocationsComponent mounted')

  // Initialize locations from store
  initializeLocations()

  // If no user-editable locations exist, create one
  const userEditableLocations = locations.value.filter((loc) => !loc.isFromTinSearch)
  if (userEditableLocations.length === 0) {
    addLocation()
  }

  // Set the first user-editable location as the initially expanded one if no other is marked
  const finalUserEditableLocations = locations.value.filter((loc) => !loc.isFromTinSearch)
  if (finalUserEditableLocations.length > 0 && !lastAddedLocationId.value) {
    lastAddedLocationId.value = finalUserEditableLocations[0].locationId
  }
})
</script>

<style scoped>
hr {
  height: 1px;
}
</style>
